import AudioPlayer from '@/components/case/AudioPlayer';
import CaseInputEditControl from '@/components/case/CaseInputEditControl';
import ExternalNoteField from '@/components/case/ExternalNoteField';
import StructuredNoteField from '@/components/case/StructuredNoteField';
import EditProtectionDialog, { useEditProtection } from '@/components/dialogs/EditProtectionDialog';
import { CardContainer } from '@/components/layout/CardContainer';
import useAlerts from '@/hooks/useAlerts';
import useCases from '@/hooks/useCases';
import { fixNoteWithLLM } from '@/services/BackendAPI';
import { isDoctor } from '@/services/featureFlag';
import { CaseStore } from '@/store/CaseStore';
import { ECaseStatus } from '@/types/ECaseStatus';
import { flexColumnFullHeight, noteFieldTextStyles } from '@/utils/theme';
import DOMPurify from 'isomorphic-dompurify';
import { observer } from 'mobx-react';
import { FC, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';

interface ICaseInputProps {}
export const CaseInput: FC<ICaseInputProps> = observer(() => {
  const cases = useCases();
  const alerts = useAlerts();
  const noteField = useRef<HTMLTextAreaElement>();
  const [fullAudioUrl, setFullAudioUrl] = useState<string>(undefined);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isStructuredVisible, setIsStructuredVisible] = useState<boolean>(true);
  const isEdit = location.pathname.includes('/edit');

  const { handleReadonlyElementClick, handleAcceptClick, handleCancelClick, readonlyDialogOpen } =
    useEditProtection(cases.isFlagged(), cases.confirmedNote);

  /**
   * Handle GenerateCodes button click.
   *
   * @returns
   */
  const handleGenerateCodesButtonClick = async () => {
    // Disable buttons to prevent clicking them twice.
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);

    if (isDoctor()) {
      await fixNoteWithLLM(cases.noteStructured.note, cases.caseId)
        .then((updatedText) => {
          cases.updateLLMEditedNote(updatedText);
          cases.updateCaseNoteSilently(updatedText);
        })
        .catch((e) => {
          console.error('Error fixing note with LLM:', e);
        });
    }
    noteField.current?.focus();
    setIsSubmitting(false);

    // Save case data.
    cases.updateCaseAndGenerateCodes().finally(() => {
      // Activate the buttons back after generating codes.
      // Show the Suggested Codes pane.
      cases.enableCodesEditMode();
    });
  };

  useEffect(() => {
    if (cases.fullAudioBlob) {
      setFullAudioUrl(window.URL.createObjectURL(cases.fullAudioBlob));
    }
  }, [cases.fullAudioBlob]);

  useEffect(() => {
    return () => {
      if (fullAudioUrl) {
        window.URL.revokeObjectURL(fullAudioUrl);
      }
    };
  }, [fullAudioUrl]);

  const areFieldsDisabled =
    (cases.isFlagged() && !cases.noteEditMode) || cases.isCompleted() || cases.confirmedNote;

  return (
    <Box
      component="form"
      sx={{
        ...flexColumnFullHeight,
        cursor: isSubmitting ? 'wait' : 'unset',
      }}
    >
      <Box sx={flexColumnFullHeight}>
        <Box
          sx={{
            ...flexColumnFullHeight,
            // Subtract player height (if necessary) and padding of the container from the full height.
            height: fullAudioUrl ? 'calc(100% - 130px - 36px)' : 'calc(100% - 36px)',
            width: '100%',
            maxWidth: '1400px',

            margin: 'auto',
          }}
          onClick={handleReadonlyElementClick}
        >
          <CardContainer
            sx={{ ...flexColumnFullHeight, height: 'calc(100% - 36px)' }}
            title="Journalnotat"
            menu={
              <ShowOriginalNoteController
                isStructuredVisible={isStructuredVisible}
                setIsStructuredVisible={setIsStructuredVisible}
                handleReadonlyElementClick={handleReadonlyElementClick}
              />
            }
          >
            <NoteFieldSection
              dictations={[]}
              cases={cases}
              isStructuredVisible={isStructuredVisible}
              areFieldsDisabled={areFieldsDisabled || isSubmitting}
            />
          </CardContainer>
          {cases.isFullAudioLoading && <AudioLoader />}
          {!cases.isFullAudioLoading && fullAudioUrl && (
            <AudioPlayer
              sx={{ pt: 0 }}
              src={fullAudioUrl}
              allowSilenceCut={[ECaseStatus.FLAGGED, ECaseStatus.SUBMITTED].includes(cases.status)}
            />
          )}
        </Box>
      </Box>

      <EditProtectionDialog
        title="Vil du redigere dit notat?"
        open={readonlyDialogOpen}
        onCancel={handleCancelClick}
        onAccept={(e) => {
          handleAcceptClick(e);
          cases.toggleNoteEditMode();
          cases.updateConfirmedNoteStatus(false);
          // For some reason, the textarea doesn't get focused here, so I need to wait for a few ms to set the focus.
          // @todo: Find a reason of why it doesn't get focused here in a normal way noteField.current?.focus() and fix if possible.
          setTimeout(() => {
            noteField.current?.focus();
          }, 100);
        }}
      />
    </Box>
  );
});

const AudioLoader: FC = () => (
  <Box sx={{ textAlign: 'center', py: 4 }}>
    <CircularProgress color="primary" />
  </Box>
);

const ShowOriginalNoteController = ({
  handleReadonlyElementClick,
}: {
  isStructuredVisible: boolean;
  setIsStructuredVisible: (isVisible: boolean) => void;
  handleReadonlyElementClick: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
    <CaseInputEditControl onClick={handleReadonlyElementClick} />
  </Box>
);

const NoteFieldSection = observer(
  ({
    dictations,
    cases,
    isStructuredVisible,
    areFieldsDisabled,
  }: {
    dictations: string[];
    cases: CaseStore;
    isStructuredVisible: boolean;
    areFieldsDisabled: boolean;
  }) => (
    <>
      {cases.isNew() && !cases.isExternalNoteIdEmpty() && !cases.isExternalNoteEmpty() && (
        <ExternalNoteField note={cases.ehrNote} />
      )}

      {isStructuredVisible ? (
        <StructuredNoteField
          dictations={dictations}
          note={cases.noteStructured}
          readOnly={areFieldsDisabled}
        />
      ) : (
        <div
          data-dd-action-name="Edit note"
          style={{
            ...(flexColumnFullHeight as any),
            // paddingLeft: '0.8rem',
            // paddingRight: '0.8rem',
            padding: 0,
            ...noteFieldTextStyles,
            overflowY: 'auto',
            width: '100%',
            whiteSpace: 'pre-wrap',
          }}
          dangerouslySetInnerHTML={{
            __html:
              cases.noteCorrected && cases.noteCorrected.length > 0
                ? DOMPurify.sanitize(cases.noteCorrected)
                : 'Not available',
          }}
        />
      )}
    </>
  ),
);
